import { Dialog } from "construct-ui";
import FileUploadInputComponent from "../../components/file/file-upload-input.component";
import { FILE_EXTENSIONS } from "../../config/constants";
import SiteManagementModel from "../../models/site-management/site-management.model";
import ViewBase from "../view-base.view";
import ByteUtils from "../../utils/byte.utils";

/**
 * Site Management - Add / Update Attachment Information Dialog View
 * 
 * A dialog page that allows users to add/update attachment information
 * 
 * @author CV Leong
 */
export default function SiteManagementAttachmentUpdateDialogView() {
  ViewBase.call(this);

  let attachmentForm = {};
  let currentSiteId = '';
  let _onClose = () => { };

  const SUPPORTED_EXT = [
    FILE_EXTENSIONS.DOC,
    FILE_EXTENSIONS.DOCX,
    FILE_EXTENSIONS.GIF,
    FILE_EXTENSIONS.JPEG,
    FILE_EXTENSIONS.JPG,
    FILE_EXTENSIONS.PDF,
    FILE_EXTENSIONS.PNG,
    FILE_EXTENSIONS.PPT,
    FILE_EXTENSIONS.PPTX,
    FILE_EXTENSIONS.XLS,
    FILE_EXTENSIONS.XLSX,
    FILE_EXTENSIONS.ZIP
  ]

  const ADD_UPDATE_FUNCTION = [
    SiteManagementModel.addSiteAttachment,
    SiteManagementModel.updateSiteAttachment,
  ]

  const DEFAULT_ATTACHMENT_FORM = {
    title: '',
    reason: '',
    file: null,
  }

  const isUpdateMode = () => !!attachmentForm?.fileId

  const formData = () => {
    const dataObj = {};
    dataObj.title = attachmentForm.title;
    dataObj.file = attachmentForm.file;

    if (isUpdateMode()) {
      dataObj.reason = attachmentForm.reason;
    }

    return dataObj;
  }

  const submit = (e) => {
    // Form validation
    if (!this.validateForm(e)) {
      return false;
    }

    addOrUpdateAttachment();
    return false;
  }

  const addOrUpdateAttachment = () => {
    this.showLoading();

    const params = {
      siteId: currentSiteId
    }

    if (isUpdateMode()) params.fileId = attachmentForm.fileId;

    ADD_UPDATE_FUNCTION[+(isUpdateMode())](params, formData())
      .then(() => {
        _onClose(true); // true: re-load contact list after closing Dialog
      })
      .catch((error) => {
        this.handleError(error);
      })
      .finally(() => {
        this.hideLoading();
      });
  }

  const loadForm = (siteId, attachmentData = DEFAULT_ATTACHMENT_FORM) => {
    attachmentForm = { ...attachmentData };
    currentSiteId = siteId;
    m.redraw();
  }

  return {
    view: ({ attrs: {
      isOpen,
      onClose,
      data,
      siteId
    } }) => {
      return [
        m(Dialog, {
          addToStack: true,
          title: data ? this.getText('LT00000890', 'Update Attachment') : this.getText('LT00000891', 'Add Attachment'),
          isOpen: isOpen,
          onClose: onClose,
          onOpened: () => { loadForm(siteId, data); _onClose = onClose; },
          closeOnOutsideClick: false,
          content: [
            m('.card',
              m('.card-body',
                m('form#addOrUpdateContactForm.row.g-4.needs-validation[novalidate]', {
                  onsubmit: submit
                },
                  m('.col-12.position-relative',
                    m('label.form-label.mandatory[for=title]', this.getText('LT00000037', 'Title')),
                    m('input.form-control', {
                      id: 'title',
                      required: true,
                      value: attachmentForm.title,
                      placeholder: this.getText('LT00000037', 'Title'),
                      onchange: (e) => {
                        attachmentForm.title = e.target.value;
                      }
                    }),
                    m('.invalid-tooltip', this.getText('LT00000729', 'This field is required')),
                  ),
                  data &&
                  m('.col-12.position-relative',
                    m('label.form-label.mandatory[for=reason]', this.getText('LT00000419', 'Reason')),
                    m('input.form-control', {
                      id: 'reason',
                      required: !!data,
                      value: attachmentForm.reason,
                      placeholder: this.getText('LT00000892', 'Reason of updating this file record'),
                      onchange: (e) => {
                        attachmentForm.reason = e.target.value;
                      }
                    }),
                    m('.invalid-tooltip', this.getText('LT00000729', 'This field is required')),
                  ),
                  m('.col-12.position-relative',
                    m('label.form-label.mandatory[for=file]', this.getText('LT00000886', 'Attachment')),
                    m(FileUploadInputComponent, {
                      id: `file`,
                      required: true,
                      accept: SUPPORTED_EXT.join(),
                      filesizeLimit: ByteUtils.mebibyteToBytes(4),
                      onInput: (fileData) => { attachmentForm.file = fileData; },
                    }),
                    m('.invalid-tooltip', this.getText('LT00000729', 'This field is required')),
                  ),
                ) // form - end
              )
            ), // card - end
            this.getBaseViews()
          ],
          footer: [
            m('.d-flex.justify-content-end.gap-2',
              m('button.btn.btn-primary[form="addOrUpdateContactForm"][type="submit"]', {
              }, this.getText('LT00000005', 'Submit')),
              m('button.btn.btn-white', {
                onclick: () => { onClose(false) },
              }, this.getText('LT00000021', 'Close')),
            )
          ],
        }),
      ];
    }
  };
}
